// utils/crypto/keyStorageSecure.ts  

import { u8ToArrayBuffer } from "./buffer";

const DB_NAME = "tl_keys_secure_db";
const STORE = "kv";
const VERSION = 1;

const PRIVATE_BLOB_KEY = "enc_priv_pem_v1";
const PUBLIC_PEM_KEY = "rsa_public_key_pem";
const DEVICE_SECRET_KEY = "device_secret_v1";

const STAGED_PRIVATE_BLOB_KEY =
  "staged_enc_priv_pem_v1";

const STAGED_PUBLIC_PEM_KEY =
  "staged_rsa_public_key_pem";

type EncryptedPrivateKeyBlob = {
  v: number;
  kdf: "PBKDF2";
  hash: "SHA-256";
  iterations: number;
  salt_b64: string;
  iv: string;
  ct: string;
};

type LegacyEncryptedPrivateKeyBlob = {
  v?: number;
  iterations?: number;
  salt_b64?: string;
  iv: string;
  ct: string;
};

type StoredPrivateKeyBlob =
  | EncryptedPrivateKeyBlob
  | LegacyEncryptedPrivateKeyBlob;

// MARK: - IndexedDB

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(
      DB_NAME,
      VERSION,
    );

    request.onupgradeneeded = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onerror = () => {
      reject(
        request.error ??
          new Error(
            "Unable to open the secure key database.",
          ),
      );
    };
  });
}

function idbGet<T = unknown>(
  key: string,
): Promise<T | null> {
  return openDB().then(
    (db) =>
      new Promise<T | null>(
        (resolve, reject) => {
          const transaction = db.transaction(
            STORE,
            "readonly",
          );

          const store =
            transaction.objectStore(STORE);

          const request = store.get(key);

          request.onsuccess = () => {
            resolve(
              (request.result as T) ?? null,
            );
          };

          request.onerror = () => {
            reject(
              request.error ??
                new Error(
                  `Unable to read secure key: ${key}`,
                ),
            );
          };
        },
      ),
  );
}

function idbSet(
  key: string,
  value: unknown,
): Promise<void> {
  return openDB().then(
    (db) =>
      new Promise<void>((resolve, reject) => {
        const transaction = db.transaction(
          STORE,
          "readwrite",
        );

        const store =
          transaction.objectStore(STORE);

        store.put(value, key);

        transaction.oncomplete = () => {
          resolve();
        };

        transaction.onerror = () => {
          reject(
            transaction.error ??
              new Error(
                `Unable to write secure key: ${key}`,
              ),
          );
        };

        transaction.onabort = () => {
          reject(
            transaction.error ??
              new Error(
                `Secure key write was aborted: ${key}`,
              ),
          );
        };
      }),
  );
}

function idbDelete(
  key: string,
): Promise<void> {
  return openDB().then(
    (db) =>
      new Promise<void>((resolve, reject) => {
        const transaction = db.transaction(
          STORE,
          "readwrite",
        );

        const store =
          transaction.objectStore(STORE);

        store.delete(key);

        transaction.oncomplete = () => {
          resolve();
        };

        transaction.onerror = () => {
          reject(
            transaction.error ??
              new Error(
                `Unable to delete secure key: ${key}`,
              ),
          );
        };

        transaction.onabort = () => {
          reject(
            transaction.error ??
              new Error(
                `Secure key deletion was aborted: ${key}`,
              ),
          );
        };
      }),
  );
}

function idbWriteMany(
  values: Array<{
    key: string;
    value?: unknown;
    remove?: boolean;
  }>,
): Promise<void> {
  return openDB().then(
    (db) =>
      new Promise<void>((resolve, reject) => {
        const transaction = db.transaction(
          STORE,
          "readwrite",
        );

        const store =
          transaction.objectStore(STORE);

        for (const item of values) {
          if (item.remove) {
            store.delete(item.key);
          } else {
            store.put(
              item.value,
              item.key,
            );
          }
        }

        transaction.oncomplete = () => {
          resolve();
        };

        transaction.onerror = () => {
          reject(
            transaction.error ??
              new Error(
                "Unable to update secure key storage.",
              ),
          );
        };

        transaction.onabort = () => {
          reject(
            transaction.error ??
              new Error(
                "Secure key storage update was aborted.",
              ),
          );
        };
      }),
  );
}

// MARK: - Binary helpers

function toBase64(
  value: Uint8Array,
): string {
  let binary = "";

  for (
    let index = 0;
    index < value.length;
    index += 1
  ) {
    binary += String.fromCharCode(
      value[index],
    );
  }

  return btoa(binary);
}

function fromBase64(
  value: string,
): Uint8Array<ArrayBuffer> {
  const binary = atob(value);
  const buffer = new ArrayBuffer(
    binary.length,
  );

  const bytes =
    new Uint8Array(buffer);

  for (
    let index = 0;
    index < binary.length;
    index += 1
  ) {
    bytes[index] =
      binary.charCodeAt(index);
  }

  return bytes;
}

function createRandomBytes(
  length: number,
): Uint8Array<ArrayBuffer> {
  const bytes = new Uint8Array(
    new ArrayBuffer(length),
  );

  crypto.getRandomValues(bytes);

  return bytes;
}

// MARK: - Device secret

export async function getOrCreateDeviceSecret(): Promise<
  Uint8Array<ArrayBuffer>
> {
  const existing =
    await idbGet<string>(
      DEVICE_SECRET_KEY,
    );

  if (existing) {
    return fromBase64(existing);
  }

  const secret =
    createRandomBytes(32);

  await idbSet(
    DEVICE_SECRET_KEY,
    toBase64(secret),
  );

  return secret;
}

// MARK: - Local AES derivation

async function deriveAesKeyFromDeviceSecret(
  secret: Uint8Array,
  salt: Uint8Array,
  iterations = 100_000,
): Promise<CryptoKey> {
  const keyMaterial =
    await crypto.subtle.importKey(
      "raw",
      u8ToArrayBuffer(secret),
      "PBKDF2",
      false,
      ["deriveKey"],
    );

  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: u8ToArrayBuffer(salt),
      iterations,
      hash: "SHA-256",
    },
    keyMaterial,
    {
      name: "AES-GCM",
      length: 256,
    },
    false,
    [
      "encrypt",
      "decrypt",
    ],
  );
}

// MARK: - Private PEM encryption

async function encryptPrivatePemForDevice(
  privatePem: string,
): Promise<EncryptedPrivateKeyBlob> {
  const secret =
    await getOrCreateDeviceSecret();

  const salt =
    createRandomBytes(16);

  const iv =
    createRandomBytes(12);

  const aesKey =
    await deriveAesKeyFromDeviceSecret(
      secret,
      salt,
      100_000,
    );

  const plaintext =
    new TextEncoder().encode(
      privatePem,
    );

  try {
    const ciphertext =
      await crypto.subtle.encrypt(
        {
          name: "AES-GCM",
          iv: u8ToArrayBuffer(iv),
        },
        aesKey,
        u8ToArrayBuffer(plaintext),
      );

    return {
      v: 2,
      kdf: "PBKDF2",
      hash: "SHA-256",
      iterations: 100_000,
      salt_b64: toBase64(salt),
      iv: toBase64(iv),
      ct: toBase64(
        new Uint8Array(ciphertext),
      ),
    };
  } finally {
    plaintext.fill(0);
    secret.fill(0);
  }
}

// MARK: - Private PEM decryption

async function decryptPrivateBlob(
  blob: StoredPrivateKeyBlob,
): Promise<string> {
  const secret =
    await getOrCreateDeviceSecret();

  try {
    if (
      Number(blob.v) >= 2 &&
      typeof blob.salt_b64 === "string" &&
      typeof blob.iv === "string" &&
      typeof blob.ct === "string"
    ) {
      const salt =
        fromBase64(blob.salt_b64);

      const iv =
        fromBase64(blob.iv);

      const ciphertext =
        fromBase64(blob.ct);

      const aesKey =
        await deriveAesKeyFromDeviceSecret(
          secret,
          salt,
          Number(blob.iterations) ||
            100_000,
        );

      const plaintext =
        await crypto.subtle.decrypt(
          {
            name: "AES-GCM",
            iv: u8ToArrayBuffer(iv),
          },
          aesKey,
          u8ToArrayBuffer(
            ciphertext,
          ),
        );

      return new TextDecoder().decode(
        plaintext,
      );
    }

    if (
      typeof blob.iv !== "string" ||
      typeof blob.ct !== "string"
    ) {
      throw new Error(
        "Invalid local private-key blob.",
      );
    }

    const legacyIv =
      fromBase64(blob.iv);

    const legacyCiphertext =
      fromBase64(blob.ct);

    const zeroSalt =
      new Uint8Array(
        new ArrayBuffer(16),
      );

    const legacyKey =
      await deriveAesKeyFromDeviceSecret(
        secret,
        zeroSalt,
        100_000,
      );

    const plaintext =
      await crypto.subtle.decrypt(
        {
          name: "AES-GCM",
          iv: u8ToArrayBuffer(
            legacyIv,
          ),
        },
        legacyKey,
        u8ToArrayBuffer(
          legacyCiphertext,
        ),
      );

    const privatePem =
      new TextDecoder().decode(
        plaintext,
      );

    // Upgrade legacy storage only after
    // successful decryption.
    await storeEncryptedPrivatePem(
      privatePem,
    );

    return privatePem;
  } finally {
    secret.fill(0);
  }
}

// MARK: - Current public key

export async function setPublicKeyPem(
  pem: string,
): Promise<void> {
  await idbSet(
    PUBLIC_PEM_KEY,
    pem,
  );
}

export async function getPublicKeyPem(): Promise<
  string | null
> {
  return idbGet<string>(
    PUBLIC_PEM_KEY,
  );
}

export async function deletePublicKeyPem(): Promise<void> {
  await idbDelete(
    PUBLIC_PEM_KEY,
  );
}

// MARK: - Current private key

export async function storeEncryptedPrivatePem(
  privatePem: string,
): Promise<void> {
  const encryptedBlob =
    await encryptPrivatePemForDevice(
      privatePem,
    );

  await idbSet(
    PRIVATE_BLOB_KEY,
    encryptedBlob,
  );

  clearPrivateCryptoKeyFromMemory();
  invalidatePrivateKeyPresenceCache();
}

export async function loadDecryptedPrivatePem(): Promise<string> {
  const blob =
    await idbGet<StoredPrivateKeyBlob>(
      PRIVATE_BLOB_KEY,
    );

  if (!blob) {
    throw new Error(
      "No private key is stored on this browser.",
    );
  }

  return decryptPrivateBlob(blob);
}

export async function hasPrivateKeyStored(): Promise<boolean> {
  return Boolean(
    await idbGet(
      PRIVATE_BLOB_KEY,
    ),
  );
}

export async function deleteEncryptedPrivatePem(): Promise<void> {
  await idbDelete(
    PRIVATE_BLOB_KEY,
  );

  clearPrivateCryptoKeyFromMemory();
  invalidatePrivateKeyPresenceCache();
}

export async function secureWipeAllKeys(): Promise<void> {
  await idbWriteMany([
    {
      key: PRIVATE_BLOB_KEY,
      remove: true,
    },
    {
      key: PUBLIC_PEM_KEY,
      remove: true,
    },
    {
      key: STAGED_PRIVATE_BLOB_KEY,
      remove: true,
    },
    {
      key: STAGED_PUBLIC_PEM_KEY,
      remove: true,
    },
  ]);

  clearPrivateCryptoKeyFromMemory();
  invalidatePrivateKeyPresenceCache();
}

// MARK: - Staged key pair

export async function stageKeyPair(
  publicPem: string,
  privatePem: string,
): Promise<void> {
  const encryptedPrivateBlob =
    await encryptPrivatePemForDevice(
      privatePem,
    );

  await idbWriteMany([
    {
      key: STAGED_PUBLIC_PEM_KEY,
      value: publicPem,
    },
    {
      key: STAGED_PRIVATE_BLOB_KEY,
      value:
        encryptedPrivateBlob,
    },
  ]);
}

export async function getStagedPublicKeyPem(): Promise<
  string | null
> {
  return idbGet<string>(
    STAGED_PUBLIC_PEM_KEY,
  );
}

export async function hasStagedKeyPair(): Promise<boolean> {
  const [
    publicPem,
    privateBlob,
  ] = await Promise.all([
    idbGet<string>(
      STAGED_PUBLIC_PEM_KEY,
    ),
    idbGet<EncryptedPrivateKeyBlob>(
      STAGED_PRIVATE_BLOB_KEY,
    ),
  ]);

  return Boolean(
    publicPem &&
      privateBlob,
  );
}

export async function discardStagedKeyPair(): Promise<void> {
  await idbWriteMany([
    {
      key: STAGED_PUBLIC_PEM_KEY,
      remove: true,
    },
    {
      key: STAGED_PRIVATE_BLOB_KEY,
      remove: true,
    },
  ]);
}

export async function commitStagedKeyPair(): Promise<void> {
  const [
    publicPem,
    privateBlob,
  ] = await Promise.all([
    idbGet<string>(
      STAGED_PUBLIC_PEM_KEY,
    ),
    idbGet<EncryptedPrivateKeyBlob>(
      STAGED_PRIVATE_BLOB_KEY,
    ),
  ]);

  if (!publicPem || !privateBlob) {
    throw new Error(
      "The staged encryption key is incomplete.",
    );
  }

  await idbWriteMany([
    {
      key: PUBLIC_PEM_KEY,
      value: publicPem,
    },
    {
      key: PRIVATE_BLOB_KEY,
      value: privateBlob,
    },
    {
      key: STAGED_PUBLIC_PEM_KEY,
      remove: true,
    },
    {
      key: STAGED_PRIVATE_BLOB_KEY,
      remove: true,
    },
  ]);

  clearPrivateCryptoKeyFromMemory();
  invalidatePrivateKeyPresenceCache();
}

// MARK: - Legacy migration

export async function migrateFromLocalStorageIfNeeded(): Promise<void> {
  if (
    typeof window === "undefined"
  ) {
    return;
  }

  const privatePem =
    localStorage.getItem(
      "rsa_private_key",
    );

  const publicPem =
    localStorage.getItem(
      "rsa_public_key",
    );

  if (privatePem) {
    await storeEncryptedPrivatePem(
      privatePem,
    );

    localStorage.removeItem(
      "rsa_private_key",
    );
  }

  if (publicPem) {
    await setPublicKeyPem(
      publicPem,
    );

    localStorage.removeItem(
      "rsa_public_key",
    );
  }
}

// MARK: - Runtime private key

let cachedPrivateKey:
  CryptoKey | null = null;

export async function getPrivateCryptoKey(): Promise<CryptoKey> {
  if (cachedPrivateKey) {
    return cachedPrivateKey;
  }

  const privatePem =
    await loadDecryptedPrivatePem();

  const {
    pemToArrayBuffer,
  } = await import("./pem");

  const pkcs8 =
    pemToArrayBuffer(privatePem);

  try {
    cachedPrivateKey =
      await crypto.subtle.importKey(
        "pkcs8",
        pkcs8,
        {
          name: "RSA-OAEP",
          hash: "SHA-256",
        },
        false,
        ["decrypt"],
      );

    return cachedPrivateKey;
  } finally {
    new Uint8Array(pkcs8).fill(0);
  }
}

export function clearPrivateCryptoKeyFromMemory(): void {
  cachedPrivateKey = null;
}

// MARK: - Presence cache

let privateKeyPresenceCache:
  boolean | null = null;

export async function hasPrivateKeyMaterial(): Promise<boolean> {
  if (
    privateKeyPresenceCache !== null
  ) {
    return privateKeyPresenceCache;
  }

  try {
    const key =
      await getPrivateCryptoKey();

    privateKeyPresenceCache =
      Boolean(key);
  } catch {
    privateKeyPresenceCache = false;
  }

  return privateKeyPresenceCache;
}

export function invalidatePrivateKeyPresenceCache(): void {
  privateKeyPresenceCache = null;
}